/**
 * User wire ⇄ domain mappers.
 *
 * Request bodies for users are assembled explicitly in `users.http.ts` (the
 * security-service contract diverges enough from the UI shape that a generic
 * snake-case pass would send unsupported fields), so this module only owns the
 * response mappers plus the username-derivation helper.
 */

import { toCamelDeep } from '@/services/http/caseMap'
import type { User, UserInvitation } from '@/types/user.types'
import { normalizeRole } from '@/security/accessContract'

function unwrapList(raw: unknown): Record<string, unknown>[] {
  const arr = Array.isArray(raw) ? raw : ((raw as { items?: unknown[] })?.items ?? [])
  return arr as Record<string, unknown>[]
}

export function mapUserFromApi(raw: Record<string, unknown>): User {
  const u = toCamelDeep<Record<string, unknown>>(raw)
  // The security service's primary key is the UUID `id`; the UI keys users by
  // `userId` (the legacy integer `user_id` is null), so map id → userId.
  if (u.id != null) u.userId = u.id
  // Prefer an explicit role; else fall back to the first of roles[].
  if (!u.role && Array.isArray(u.roles) && u.roles.length > 0) {
    u.role = u.roles[0]
  }
  u.role = normalizeRole(u.role)
  if (Array.isArray(u.roles)) u.roles = u.roles.map(normalizeRole)
  return u as unknown as User
}

export function mapUserListFromApi(raw: unknown): User[] {
  return unwrapList(raw).map(mapUserFromApi)
}

export function mapInvitationFromApi(raw: Record<string, unknown>): UserInvitation {
  const invitation = toCamelDeep<UserInvitation>(raw)
  invitation.role = normalizeRole(invitation.role)
  return invitation
}

export function mapInvitationListFromApi(raw: unknown): UserInvitation[] {
  return unwrapList(raw).map(mapInvitationFromApi)
}

/**
 * Derive a security-service-valid username from an email address.
 *
 * The backend allows only letters/digits/`_`/`.` (after stripping `_`/`.` the
 * remainder must be alphanumeric) and requires ≥ 3 chars. Using the whole email
 * (with `@` → `.`) keeps usernames unique, since emails are unique per tenant.
 */
export function deriveUsername(email: string): string {
  const cleaned = email
    .toLowerCase()
    .replace(/[^a-z0-9_.]/g, '.') // `@` and any other char → `.`
    .replace(/\.+/g, '.') // collapse runs of dots
    .replace(/^\.+|\.+$/g, '') // trim leading/trailing dots
  return cleaned.length >= 3 ? cleaned : `user.${cleaned}`
}
